import { doc, getDoc, getFirestore, runTransaction, serverTimestamp } from 'firebase/firestore';

/**
 * Internal reviewer notes for a cloud eval.
 *
 * Stored in their own collection, keyed by evaluation id, NOT on the
 * cloud_eval_results doc: vasi_functions reprocessing rewrites that doc with
 * a non-merge set(), which would wipe notes. Nothing in the report/PDF
 * pipeline reads this collection.
 */
export const CLOUD_EVAL_NOTES_COLLECTION = 'cloud_eval_notes';
export const NOTES_MAX_LENGTH = 20000;
export const NOTES_CONFLICT_MESSAGE = 'These notes were changed by someone else. Reload to see their version.';

export class NotesConflictError extends Error {
  constructor(message = NOTES_CONFLICT_MESSAGE) {
    super(message);
    this.name = 'NotesConflictError';
  }
}

/** Trailing whitespace is dropped, so whitespace-only notes count as empty. */
export function normalizeNotes(notes) {
  return typeof notes === 'string' ? notes.replace(/\s+$/, '') : '';
}

function timestampMillis(value) {
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  return null;
}

/** Compare Firestore Timestamps; absent (null/undefined) only equals absent. */
export function sameTimestamp(left, right) {
  const a = left ?? null;
  const b = right ?? null;
  if (a === null || b === null) return a === b;
  if (typeof a.isEqual === 'function' && a.isEqual(b)) return true;
  const aMillis = timestampMillis(a);
  return aMillis !== null && aMillis === timestampMillis(b);
}

function notesRef(db, evaluationId) {
  return doc(db, CLOUD_EVAL_NOTES_COLLECTION, evaluationId);
}

function toNotesState(snapshot) {
  const data = snapshot?.exists?.() ? snapshot.data() : null;
  return {
    notes: typeof data?.notes === 'string' ? data.notes : '',
    notesUpdatedAt: data?.notesUpdatedAt ?? null,
    notesUpdatedBy: data?.notesUpdatedBy ?? null,
  };
}

/** A missing notes doc means the eval simply has no notes yet. */
export async function loadEvalNotes({ evaluationId, db = getFirestore() } = {}) {
  if (!evaluationId) {
    throw new Error('Cannot load notes without an evaluation id.');
  }
  return toNotesState(await getDoc(notesRef(db, evaluationId)));
}

/**
 * Saves notes in a transaction. If the doc's notesUpdatedAt no longer matches
 * `expectedUpdatedAt` (what the panel loaded), someone else saved in between
 * and we refuse to overwrite their version.
 *
 * Returns the committed state, re-read so notesUpdatedAt is the real server
 * timestamp for the next conflict check. `confirmed` is false if that re-read
 * failed (the save itself still succeeded).
 */
export async function saveEvalNotes({
  evaluationId,
  notes,
  userId,
  expectedUpdatedAt = null,
  db = getFirestore(),
} = {}) {
  if (!evaluationId) {
    throw new Error('Cannot save notes without an evaluation id.');
  }
  if (typeof notes !== 'string') {
    throw new Error('Notes must be a string.');
  }
  const normalized = normalizeNotes(notes);
  if (normalized.length > NOTES_MAX_LENGTH) {
    throw new Error(`Notes must be ${NOTES_MAX_LENGTH.toLocaleString('en-US')} characters or fewer.`);
  }

  const reference = notesRef(db, evaluationId);
  await runTransaction(db, async (transaction) => {
    const current = toNotesState(await transaction.get(reference));
    if (!sameTimestamp(current.notesUpdatedAt, expectedUpdatedAt)) {
      throw new NotesConflictError();
    }
    transaction.set(reference, {
      evaluationId,
      notes: normalized,
      notesUpdatedAt: serverTimestamp(),
      notesUpdatedBy: userId ?? null,
    });
  });

  try {
    return { ...(await loadEvalNotes({ evaluationId, db })), confirmed: true };
  } catch (err) {
    console.error('Saved evaluation notes but could not re-read them:', err);
    return { notes: normalized, notesUpdatedAt: null, notesUpdatedBy: userId ?? null, confirmed: false };
  }
}
